import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import React from "react";
import classNames from "classnames";

import {TaskSchedule} from "../../../stores/taskLists/TaskSchedule";
import {Select, SelectOption} from "../../components/form/Select";
import {CheckBoxIcon} from "../../components/icons/CheckBoxIcon";
import {MonthDaysSelect} from "../../components/form/CalendarSelect/MonthDaysSelect";
import {CustomIntervalInput} from "./CustomIntervalInput";
import {InfoIcon} from "../../components/icons/InfoIcon";
import {FormHint} from "../../components/FormHint";
import {Button} from "../../components/Button";
import {YearDaysSelect} from "../../components/form/CalendarSelect/YearDaysSelect";


import {WEEK_DAYS_SHORT, getWeekDayOrder} from "../../../utils/date";
import {screenSizeProvider} from "../../../stores/ScreenSizeProvider";


export interface ScheduleRepeatTaskProps {
    schedule: TaskSchedule;
}

export enum ScheduleRepeatInterval {
    EVERY_DAY = "every-day",
    EVERY_WEEK = "every-week",
    EVERY_OTHER_WEEK = "every-other-week",
    EVERY_MONTH = "every-month",
    EVERY_YEAR = "every-year",
    CUSTOM = "custom-interval",
}

export const ScheduleRepeatTask = observer((
    {
        schedule
    }: ScheduleRepeatTaskProps
) => {
    const isMobile = screenSizeProvider.view !== 'desktop';

    const RepeatTaskOptions: SelectOption[] = [
        {value: ScheduleRepeatInterval.EVERY_DAY, label: intl.get("common.everyDay").d("Every day")},
        {value: ScheduleRepeatInterval.EVERY_WEEK, label: intl.get("common.everyWeek").d("Every week")},
        {
            value: ScheduleRepeatInterval.EVERY_OTHER_WEEK,
            label: intl.get("common.everyTwoWeeks").d("Every two weeks")
        },
        {value: ScheduleRepeatInterval.EVERY_MONTH, label: intl.get("common.everyMonth").d("Every month")},
        {value: ScheduleRepeatInterval.EVERY_YEAR, label: intl.get("common.everyYear").d("Every year")},
        {value: ScheduleRepeatInterval.CUSTOM, label: intl.get("common.customInterval").d("Custom Interval")},
    ] as const;

    const renderRepeatTaskOptionsBlock = () => {
        switch (schedule.repeatOption) {
            case ScheduleRepeatInterval.EVERY_DAY:
                return null;
            case ScheduleRepeatInterval.EVERY_WEEK:
            case ScheduleRepeatInterval.EVERY_OTHER_WEEK:
                return (
                    <div className="ScheduleSetting__repeatTask__weekdays">
                        {getWeekDayOrder().map((dayIdx) => (
                            <div
                                key={dayIdx}
                                className={classNames({
                                    "ScheduleSetting__repeatTask__WeekDayButton body-large": true,
                                    "ScheduleSetting__repeatTask__WeekDayButton--selected": schedule.selectedWeekDays.includes(dayIdx)
                                })}
                                onClick={() => schedule.toggleWeekDay(dayIdx)}
                            >
                                {WEEK_DAYS_SHORT[dayIdx]}
                            </div>
                        ))}
                        <div className="ScheduleSetting__checkbox">
                            <CheckBoxIcon
                                className={classNames({
                                    "ScheduleSetting__checkbox": true,
                                    "TaskList_checkbox_alt": schedule.selectedWeekDays.length === 7,
                                    "TaskList_checkbox_normal": schedule.selectedWeekDays.length !== 7,
                                })}
                                onClick={() => schedule.toggleAllWeekDays(schedule.selectedWeekDays.length !== 7)}
                                size={24}
                                isChecked={schedule.selectedWeekDays.length === 7}
                            />
                            <div className="ScheduleSetting__repeatTask__text">
                                {intl.get('common.all').d('All')}
                            </div>
                        </div>
                    </div>
                );
            case ScheduleRepeatInterval.EVERY_MONTH:
                return (
                    <div className="ScheduleSetting__repeatTask_nowrap">
                        <span style={{textWrap: 'nowrap'}}>{intl.get('common.on').d('on:')}</span>
                        <MonthDaysSelect
                            id={'schedule'}
                            className="ScheduleSetting__repeatTask__month"
                            key="month"
                            placeholder={intl.get("calendar-select.placeholder").d("Select days")}
                            withBackground={false}
                            selectedDays={schedule.selectedMonthDates}
                            onChange={schedule.setSelectedMonthDates}
                        />
                    </div>
                );
            case ScheduleRepeatInterval.EVERY_YEAR:
                return (
                    <div className="ScheduleSetting__repeatTask_nowrap">
                        <span style={{textWrap: 'nowrap'}}>{intl.get('common.on').d('on:')}</span>
                        <div className="ScheduleSetting__repeatTask__year">
                            <YearDaysSelect
                                key="year"
                                placeholder={intl.get("calendar-select.placeholder").d("Select days")}
                                withBackground={false}
                                selectedDates={schedule.selectedYearDates}
                                onChange={schedule.setSelectedYearDates}
                            />
                            {schedule.selectedYearDates.length > 0 ? (
                                <Button
                                    className="Calendar__Repeat__Year__Button"
                                    text={intl.get("Calendar-Repeat.Month.button").d("Clear All")}
                                    type="primary"
                                    style="inline"
                                    size="sm"
                                    onClick={() => schedule.setSelectedYearDates([])}
                                />
                            ) : null}
                        </div>
                    </div>
                );
            case ScheduleRepeatInterval.CUSTOM:
                return (
                    <div className="ScheduleSetting__repeatTask__customInterval">
                        <CustomIntervalInput value={schedule.customInterval} onChange={schedule.setCustomInterval}/>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="ScheduleSetting__repeatTask__block">
            <div className="ScheduleSetting__content__title title-medium">
                {intl.get('ScheduleSetting.repeat.task').d("Repeat this task")}
            </div>
            <div className="ScheduleSetting__repeatTask__optionBlock">
                <Select
                    className="ScheduleSetting__repeatTask__select"
                    options={RepeatTaskOptions}
                    selectedValue={schedule.repeatOption}
                    onChange={(value) => schedule.setRepeatOption(value as ScheduleRepeatInterval)}
                    size="medium"
                    fontSize="body-large"
                    ignoreActiveHighlight={true}
                    style={'select2'}
                    placeholder={intl.get("common.chooseOption").d("Choose an option")}
                    selectBoxMinWidth={180}
                />
                {renderRepeatTaskOptionsBlock()}
            </div>
            {
                !isMobile && schedule.repeatOption === "custom-interval"
                    ? (
                        <FormHint
                            className="CustomInterval__hint"
                            size={"small"}
                            icon={<InfoIcon size={16}/>}
                            text={intl.get("custom-interval.hint").d(
                                "For example, the task will repeat for 2 days, then pause for 3 days, and repeat again."
                            )}
                        />
                    )
                    : null
            }
        </div>
    );
});
